import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { expeditionService } from "../services/expedition";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { formatDateForDisplay } from "../utils/dateUtils";
import { extractPackMultiplier, getUnitWeight } from "../utils/packUtils";

const ExpeditionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expedition, setExpedition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Função auxiliar para calcular as unidades individuais a partir do peso total
  // expedited_quantity está em kg (peso total = unidades_individuais × peso_unitário)
  const calculateUnits = (item) => {
    if (
      !item.products ||
      item.expedited_quantity === undefined ||
      item.expedited_quantity === null
    )
      return 0;
    const productWeightPerUnit = getUnitWeight(item.products);

    // Se o peso por unidade for desconhecido, ou o item pesar menos que uma
    // unidade, mostrar sempre pelo menos 1 unidade (esta página é só de
    // visualização, não faz divisão).
    if (
      productWeightPerUnit === undefined ||
      productWeightPerUnit === null ||
      productWeightPerUnit === 0
    )
      return 1;

    // Unidades individuais = peso total / peso por unidade individual
    return Math.max(1, item.expedited_quantity / productWeightPerUnit);
  };

  // Função auxiliar para formatar valores
  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null) return "0";
    return Number(value).toFixed(decimals);
  };

  const statusMap = {
    processing: "Em Processamento",
    pending: "Pendente",
    packed: "Embalado",
    shipped: "Enviado",
    delivered: "Entregue",
    cancelled: "Cancelado",
    divided: "Dividida",
  };

  const originMap = {
    api: "Via API",
    manual: "Criada manualmente",
  };

  const fetchExpeditionDetails = async () => {
    try {
      setLoading(true);
      const data = await expeditionService.getExpeditionById(id);
      setExpedition(data);
      // Remove initialization of split quantities since we're handling individual items

      // Adiciona as unidades calculadas aos itens da expedição para fácil acesso
      const updatedExpeditionItems = data.expedition_items.map((item) => ({
        ...item,
        calculated_units: calculateUnits(item),
      }));
      setExpedition((prev) => ({
        ...prev,
        expedition_items: updatedExpeditionItems,
      }));

      setError("");
    } catch (err) {
      // Em caso de erro, definir expedition como null (não mostrar erro)
      console.error("Erro ao carregar detalhes da expedição:", err);
      setExpedition(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchExpeditionDetails();
    }
  }, [id]);


  if (loading) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 text-center">
        Carregando detalhes da expedição...
      </div>
    );
  }

  if (!expedition) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 text-center">
        <div className="text-neutral-500">
          <p className="text-lg mb-2">Expedição não encontrada</p>
          <p className="text-sm mb-6">A expedição solicitada não existe ou foi removida</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl">
          Detalhes da Expedição #{expedition.expedition_number}
        </h1>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate(`/edit-expedition/${id}`)}
            className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
          >
            Editar Expedição
          </button>
          <button className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800">
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
            Exportar Relatório
          </button>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 border-b pb-2">
            Informações da Encomenda
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-neutral-500">Nº da Encomenda</p>
              <p className="font-medium">{expedition.orders?.order_number}</p>
            </div>
            <div>
              <p className="text-neutral-500">Cliente</p>
              <p className="font-medium">{expedition.orders?.client_name}</p>
            </div>
            <div>
              <p className="text-neutral-500">NIF do Cliente</p>
              <p className="font-medium">{expedition.orders?.client_nif}</p>
            </div>
            <div>
              <p className="text-neutral-500">Data de Entrega</p>
              <p className="font-medium">
                {formatDateForDisplay(expedition.orders?.delivery_date)}
              </p>
            </div>
            <div className="md:col-span-2">
              <p className="text-neutral-500">Endereço de Destino</p>
              <p className="font-medium">
                {expedition.orders?.destination_address}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 border-b pb-2">
            Informações da Expedição
          </h2>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-neutral-500">Status</p>
              <p className="font-medium">
                {statusMap[expedition.status] || expedition.status}
              </p>
            </div>
            <div>
              <p className="text-neutral-500">Origem</p>
              <p className="font-medium">
                {originMap[expedition.origin_source] || "Não informado"}
              </p>
            </div>
            <div>
              <p className="text-neutral-500">Transportadora</p>
              <p className="font-medium">{expedition.shipping_company}</p>
            </div>
            <div>
              <p className="text-neutral-500">Nº da Fatura</p>
              <p className="font-medium">{expedition.invoice_number}</p>
            </div>
            <div>
              <p className="text-neutral-500">Embalado</p>
              <p className="font-medium">
                {expedition.is_packed ? "Sim" : "Não"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold p-6">Itens Expedidos</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Código do Produto
                </th>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Lote
                </th>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Unidades
                </th>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Peso Total (kg)
                </th>
                <th className="px-6 py-3 text-left text-sm text-neutral-600">
                  Tipo de Caixa
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {expedition.expedition_items &&
              expedition.expedition_items.length > 0 ? (
                expedition.expedition_items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 text-sm">{item.products?.name}</td>
                    <td className="px-6 py-4 text-sm">
                      {item.products?.product_code}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {item.production_lots ? (
                        <div>
                          <div className="font-medium">{item.production_lots.lot_number}</div>
                          <div className="text-xs text-neutral-500">
                            {formatDateForDisplay(item.production_lots.production_date)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-neutral-400">Sem lote definido</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(() => {
                        const individualUnits = calculateUnits(item);
                        const packMultiplier = extractPackMultiplier(item.products?.name);
                        if (packMultiplier > 1) {
                          const packs = individualUnits / packMultiplier;
                          return `${packs.toFixed(0)} pack${packs !== 1 ? 's' : ''} (${individualUnits.toFixed(0)} unidades)`;
                        }
                        return `${(individualUnits || 0).toFixed(0)} unidades`;
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {(item.expedited_quantity || 0).toFixed(2)} kg
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {item.box_type || <span className="text-neutral-400">Não definido</span>}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-4 text-center text-neutral-500"
                  >
                    Nenhum item nesta expedição.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default ExpeditionDetails;
